import { JBaseApi } from '../base';
import { OF } from '../../util/network';
import { jObjectLittle } from '../../types/objectBase';
import { Rights } from '../../types/common';
import { jArticleLittle, jArticleView, jFolderList } from '../../types/article';
import { ApiPagingResults, ApiWrapper, FormatList } from '../../types/utils';
import { jGroupLittle } from '../../types/group';
import { jFolderLittle, jObjectList } from '../../types/object';

export interface RootFolderProps {
    id: number;
    title: string;
    uri: string;
    type: 'rootFolder';
    mainType: string;
    folderColor?: string;
}

export interface SpotProps {
    id: number;
    title: string;
    uri: string;
    type: string;
    mainType: string;
    folderColor?: string;
}

export interface FileBankProps {
    recents: jObjectList[];
    rootFolder: jFolderList;
    spot: jGroupLittle;
}

export interface FolderProps extends jObjectLittle {
    mainType: string;
    folderColor?: string;
    parentURI?: string;
    _right: Rights;
}

export type FilebankGetPathParams = {
    uri: string;
    mode?: 'full' | 'browse';
};
export type FilebankSearchContentParams = {
    uri: string;
    query: string;
    limit?: number;
    page?: number;
};

export type FilebankPath<T extends jArticleLittle = jArticleLittle> = {
    [k: string]: T & { _nbItems: number };
};

export type FilebankGetParentsParams = {
    id: number;
};

export class JFilebankApi extends JBaseApi {
    public getBanks(options?: { format?: FormatList; signal?: AbortSignal }): Promise<Array<FileBankProps>> {
        return this.network
            .post<Array<FileBankProps>>(
                {
                    o: 'fileBank',
                    f: 'getBanks',
                    format: options?.format ?? 'raw-list',
                } as OF,
                options?.signal,
            )
            .then((res) => {
                return res.result;
            });
    }

    public getFolders(
        parentURI: string,
        options?: {
            format?: FormatList;
            signal?: AbortSignal;
        },
    ): Promise<ApiWrapper<jFolderList[]>> {
        return this.network.post(
            {
                o: 'fileBank',
                f: 'getFolders',
                parentURI,
                format: options?.format ?? 'raw-list',
            } as OF,
            options?.signal,
        );
    }

    public getDocuments(
        uri: string,
        options?: {
            format?: FormatList;
            signal?: AbortSignal;
        },
    ): Promise<ApiPagingResults<jArticleView>> {
        return this.network.post(
            {
                o: 'fileBank',
                f: 'getDocuments',
                parentURI: uri,
                format: options?.format ?? 'raw-view',
            } as OF,
            options?.signal,
        );
    }

    public getPath<T extends jArticleLittle = jArticleLittle>(
        params: FilebankGetPathParams,
        options?: { format?: FormatList; signal?: AbortSignal },
    ): Promise<ApiWrapper<FilebankPath<T>>> {
        return this.network.post(
            {
                o: 'fileBank',
                f: 'getPath',
                uri: params.uri,
                mode: params.mode ?? 'browse',
                format: options?.format ?? 'raw-little',
            } as OF,
            options?.signal,
        );
    }

    public searchContent<T extends jArticleLittle = jArticleLittle>(
        params: FilebankSearchContentParams,
        options?: { format?: FormatList; signal?: AbortSignal },
    ): Promise<ApiPagingResults<T>> {
        return this.network.post(
            {
                o: 'fileBank',
                f: 'searchContent',
                ...params,
                format: options?.format ?? 'raw-little',
            } as OF,
            options?.signal,
        );
    }

    public getParents<T extends jArticleLittle = jArticleLittle>(
        params: FilebankGetParentsParams,
        options?: {
            format?: FormatList;
            signal?: AbortSignal;
        },
    ): Promise<ApiWrapper<T[]>> {
        return this.network.post(
            {
                o: 'fileBank',
                f: 'getParents',
                id: params.id,
                format: options?.format ?? 'raw-little',
            } as OF,
            options?.signal,
        );
    }

    public getRootFolders<T extends jFolderLittle = jFolderList>(
        params: { query?: string; page?: number; limit?: number },
        options?: {
            format?: FormatList;
            signal?: AbortSignal;
        },
    ): Promise<ApiPagingResults<T>> {
        return this.network.post(
            {
                o: 'objectList',
                f: 'fileBankRootFolders',
                query: params.query,
                format: options?.format ?? 'raw-little',
            } as OF,
            options?.signal,
        );
    }
}
